import type { IconSpec } from '@hibro/shared';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Icon,
} from '@hibro/ui-client';
import type { ReactNode } from 'react';

import type { MenuEntry } from '../../commands/menu-entries';
import { useTranslator } from '../../i18n/i18n';
import menuDivider from './assets/divider.svg?url';

/**
 * 稿 `93:7592` Menu：292 宽（描边不占布局，行才能落到 280）、14 圆角、6 内边距、
 * 白描边（`Color/stroke/gray100`，与面同色，用内阴影画，不把内容挤窄）、
 * 阴影 `0 0 8px rgba(0,0,0,0.12)`。`border-0` 盖掉弹层默认的 1px `border`。
 */
const CREATE_MENU_SURFACE_CLASS =
  'app-font w-[292px] rounded-[14px] border-0 bg-(--app-color-bg-surface) p-[6px] shadow-[0_0_8px_rgba(0,0,0,0.12),inset_0_0_0_1px_var(--app-color-bg-surface)] backdrop-blur-[30px]';
/**
 * 行 64 高：上 12 + 内容 40 + 下 12。圆角首行 10、其余 8。悬停底是
 * `Color/background/meun-hover`（`--app-color-card-hover`）。字色必须写回
 * primary：组件默认的 `focus/data-highlighted:text-accent-foreground` 会把
 * 标题和 `currentColor` 图标染成强调色。
 */
const CREATE_MENU_ROW_CLASS =
  'group relative h-16 w-full items-start gap-3 rounded-lg px-3 py-3 text-left text-(--app-color-text-primary) outline-none first:rounded-[10px] focus:bg-(--app-color-card-hover) focus:text-(--app-color-text-primary) data-highlighted:bg-(--app-color-card-hover) data-highlighted:text-(--app-color-text-primary) data-popup-open:bg-(--app-color-card-hover) data-popup-open:text-(--app-color-text-primary)';

const FALLBACK_ICON: IconSpec = { type: 'builtin', id: 'i-command' };

export interface HeaderCreateProps {
  /** Rows for `MENU_LOCATION.headerCreate`, already resolved and ordered. */
  entries: readonly MenuEntry[];
  onRun: (command: string) => void;
}

/**
 * The header's Create button and its menu (见 03 header 槽位).
 *
 * The first consumption face for `contributes.menus`: what shows up here is
 * whichever commands and submenus modules placed at
 * `MENU_LOCATION.headerCreate`, not a list the platform keeps. That is the
 * whole reason this needed no new field on the command contract — "surface
 * this command at that entry point" is what a menu placement already says,
 * with its own `when` on top of the command's `enablement`.
 *
 * A menu button (Base UI `Menu`), not the search box's combobox: nothing here
 * is typed into or filtered, and the APG pattern picks the primitive (见 `16`).
 * Nested folders reuse `DropdownMenuSub`; their children were already resolved by
 * `resolveMenuLocation`, so this face only draws the tree.
 *
 * No entries, no button: the same occupancy rule the app tab strip and the
 * auxiliaryBar follow (见 03). It also means a mistyped location key reads as
 * "the button vanished" rather than a button that opens onto nothing.
 */
export function HeaderCreate({ entries, onRun }: HeaderCreateProps): ReactNode {
  const { t } = useTranslator();
  if (entries.length === 0) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            title={t('workbench.action.createNew')}
            aria-label={t('workbench.action.createNew')}
            style={{ WebkitAppRegion: 'no-drag' }}
            className="h-6 gap-1 rounded-lg bg-primary px-2.5 text-primary-foreground cursor-pointer"
          />
        }
      >
        <Icon className="size-3" spec={{ type: 'builtin', id: 'add' }} />
        {t('workbench.action.createNew')}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={CREATE_MENU_SURFACE_CLASS}>
        <CreateMenuItems entries={entries} onRun={onRun} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CreateMenuItems({
  entries,
  onRun,
}: {
  entries: readonly MenuEntry[];
  onRun: (command: string) => void;
}): ReactNode {
  return entries.map((entry, index) =>
    entry.kind === 'submenu' ? (
      <DropdownMenuSub key={`submenu:${entry.submenu}`}>
        <DropdownMenuSubTrigger className={CREATE_MENU_ROW_CLASS}>
          <CreateMenuRowContent entry={entry} />
          {index < entries.length - 1 ? <CreateMenuDivider /> : null}
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className={CREATE_MENU_SURFACE_CLASS}>
          <CreateMenuItems entries={entry.children} onRun={onRun} />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    ) : (
      <DropdownMenuItem
        key={entry.command}
        className={CREATE_MENU_ROW_CLASS}
        onClick={() => onRun(entry.command)}
      >
        <CreateMenuRowContent entry={entry} />
        {index < entries.length - 1 ? <CreateMenuDivider /> : null}
      </DropdownMenuItem>
    ),
  );
}

function CreateMenuDivider(): ReactNode {
  return (
    <img
      src={menuDivider}
      alt=""
      className="pointer-events-none absolute bottom-0 left-0 group-focus:opacity-0 group-data-highlighted:opacity-0"
    />
  );
}

function CreateMenuRowContent({ entry }: { entry: MenuEntry }): ReactNode {
  return (
    <>
      <span
        aria-hidden="true"
        className="relative flex size-6 shrink-0 items-center justify-center"
      >
        <Icon
          size={24}
          spec={entry.icon ?? FALLBACK_ICON}
          className="text-(--app-color-text-primary)"
        />
      </span>
      <span className="flex h-10 min-w-0 flex-1 flex-col">
        <span className="truncate text-[14px] leading-[22px] font-normal">
          {entry.title}
        </span>
        {entry.kind === 'command' && entry.subtitle ? (
          <span className="truncate text-[12px] leading-[18px] font-normal text-(--app-color-text-tertiary)">
            {entry.subtitle}
          </span>
        ) : null}
      </span>
    </>
  );
}
